import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import { ApiErrorException } from '@/api/client';
import { Button } from '@/components/ui/button';
import { copyText } from '@/components/shared/CopyLink';
import * as RG from '@/components/ui/radio-group';
import * as CB from '@/components/ui/checkbox';

/**
 * Small pieces shared by the super admin's platform pages (W6: Twilio,
 * Détection de répondeur, Opérateur des appels).
 */

/** The page's single <h1>, named like its menu entry (DESIGN.md §5). */
export function PlatformPageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--vf-text-1)' }}>{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm" style={{ color: 'var(--vf-text-2)' }}>{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Code of a failed request, when the server gave one. */
export function errorCode(err: unknown): string | undefined {
  if (err instanceof ApiErrorException) return err.code || `HTTP ${err.status}`;
  return undefined;
}

/**
 * DES-16: the translated message first, then the code in small monospace with
 * a copy button, then « Réessayer » when there is something to retry.
 */
export function ErrorLine({ message, error, onRetry }: { message: ReactNode; error?: unknown; onRetry?: () => void }) {
  const { t } = useTranslation(['common']);
  const code = errorCode(error);
  return (
    <div role="alert" className="space-y-1 text-sm">
      <p style={{ color: 'var(--vf-fill-danger)' }}>{message}</p>
      {code && (
        <p className="flex items-center gap-1 font-mono text-[11px]" style={{ color: 'var(--vf-text-3)' }}>
          <span>{code}</span>
          <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-[var(--vf-surface-3)]" aria-label={t('common:dataTable.copyCode')} onClick={() => void copyText(code)}>
            <Copy className="h-3.5 w-3.5" aria-hidden />
          </button>
        </p>
      )}
      {onRetry && <Button type="button" variant="outline" size="sm" onClick={onRetry}>{t('common:actions.retry')}</Button>}
    </div>
  );
}

// components/ui/radio-group and checkbox are untyped JS; the props used here.
export const RadioGroup = RG.RadioGroup as unknown as ComponentType<{ value: string; onValueChange: (value: string) => void; className?: string; children?: ReactNode; 'aria-label'?: string }>;
export const RadioGroupItem = RG.RadioGroupItem as unknown as ComponentType<{ value: string; id: string; className?: string; 'aria-describedby'?: string }>;
export const Checkbox = CB.Checkbox as unknown as ComponentType<{ id: string; checked: boolean; onCheckedChange: (checked: boolean | 'indeterminate') => void; 'aria-label'?: string }>;
